import { Component, OnInit, OnDestroy, OnChanges, SimpleChanges, Input, ChangeDetectorRef, Output, EventEmitter } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { select } from '@ngrx/store';
import { AppState } from 'src/app/shareModule/states/app.state';
import { selectDiscountData } from 'src/app/shareModule/states/nearby';
import { loadDiscount } from 'src/app/shareModule/states/nearby';
import { Subject, takeUntil } from 'rxjs';
import { trigger, transition, style, animate } from '@angular/animations';
import { environment } from 'src/environments/environment';
import { CustomerService } from 'src/app/shareModule/services/customer.service';
import { CommonService } from 'src/app/shareModule/services/common.service';

@Component({
  selector: 'app-story-kachiing',
  templateUrl: './story-kachiing.component.html',
  styleUrl: './story-kachiing.component.scss',
  animations: [
    trigger('pageTransition', [
      transition('* => forward', [
        style({ opacity: 0, transform: 'translateX(100%)' }),
        animate('0.4s ease-in-out', style({ opacity: 1, transform: 'translateX(0)' }))
      ]),
      transition('* => backward', [
        style({ opacity: 0, transform: 'translateX(-100%)' }),
        animate('0.4s ease-in-out', style({ opacity: 1, transform: 'translateX(0)' }))
      ])
    ]),
    trigger('fadeInOut', [
      transition('* => *', [
        style({ opacity: 0, transform: 'translateY(20px)' }),
        animate('0.5s ease-in-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ])
    ])
  ]
})
export class StoryKachiingComponent implements OnInit, OnDestroy, OnChanges {
  @Input() filter: string = '';
  @Input() isBookingNow: boolean = false;
  @Input() selectedMapCategory: any = null;
  @Input() currentSelectedTypes: any[] = [];
  @Output() openShowBookingsModal = new EventEmitter<void>();
  @Output() openWhatsApp = new EventEmitter<void>();
  public defaultDescription: string = "Find something good > Reinforces that Biz<br>Don't find anything good > Recommend for next person to benefit";

  private destroy$ = new Subject<void>();
  public contentList: any[] = [];
  public currentPageIndex: number = 0;
  public animationDirection: 'forward' | 'backward' = 'forward';
  public bizTags: string[] = [];
  public userTags: string[] = [];
  public selectedTags = new Set<string>();
  public showTags: boolean = true;
  public imageUrl: string = environment.ImageUrl;
  public bookNowText: string = '';
  constructor(
    public activeModal: NgbActiveModal,
    private store: Store<AppState>,
    private cdr: ChangeDetectorRef,
    private customerService: CustomerService,
    private commonService: CommonService
  ) { }

  isIconImage(): boolean {
    return this.displayIcon && this.displayIcon.includes('public/');
  }

  getIconImageUrl(): string {
    if (this.isIconImage()) {
      return this.imageUrl + this.displayIcon;
    }
    return '';
  }

  ngOnInit(): void {
    // Load discount data when filter is available
    if (this.filter) {
      this.store.dispatch(loadDiscount({ filter: this.filter }));
    }

    // Subscribe to discount data from store
    this.store
      .pipe(
        select(selectDiscountData),
        takeUntil(this.destroy$)
      )
      .subscribe((discountData: any) => {
        if (discountData) {
          this.contentList = discountData.contentLists || [];
          this.bookNowText = discountData.bookNowText || 'Book Now';
          // Global showTags setting (for backward compatibility, but now each content item has its own)
          this.showTags = discountData.showTags !== undefined ? discountData.showTags : true;

          this.currentPageIndex = 0;
          this.cdr.detectChanges();
        }
      });

    // Load tags on init
    this.loadTags();
  }

  private loadTags(): void {
    const selectedCategory = this.currentSelectedTypes?.length
      ? this.currentSelectedTypes[0]
      : this.selectedMapCategory;

    if (selectedCategory?.bookingMoreInfos) {
      this.bizTags = selectedCategory.bookingMoreInfos.tagChoices ?? [];
      this.userTags = selectedCategory.bookingMoreInfos.tagUserChoices ?? [];
    } else {
      this.bizTags = [];
      this.userTags = [];
    }

    this.customerService.getMapTags({
      filter: this.filter,
      ipAddress: this.commonService.clientIp
    }).subscribe((res: any) => {
      if (res.body) {
        this.selectedTags = new Set(res.body || []);
        this.cdr.detectChanges();
      }
    });

    this.cdr.detectChanges();
  }

  public bookingTagClicked(event: Event, tag: string): void {
    event.stopPropagation();
    event.preventDefault();
    if (this.selectedTags.has(tag)) {
      this.selectedTags.delete(tag);
    } else {
      this.selectedTags.add(tag);
    }

    this.customerService.addMapTag({
      filter: this.filter,
      tag: tag,
      ipAddress: this.commonService.clientIp
    }).subscribe((res: any) => {
      if (res) {
        this.loadTags();
      }
    });

    this.cdr.detectChanges();
  }

  public trackByTag(index: number, tag: string): string {
    return tag;
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Reload tags when selectedMapCategory or currentSelectedTypes change
    if (changes['selectedMapCategory'] || changes['currentSelectedTypes']) {
      this.loadTags();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  close(): void {
    this.activeModal.close();
  }

  gotIt(): void {
    this.openWhatsApp.emit();
    // this.activeModal.close();
  }

  bookNow(): void {
    this.openShowBookingsModal.emit();
    this.activeModal.close();
  }

  nextPage(): void {
    if (this.hasNextPage()) {
      this.animationDirection = 'forward';
      this.currentPageIndex++;
      this.cdr.detectChanges();
    }
  }

  hasNextPage(): boolean {
    return this.currentPageIndex < this.contentList.length - 1;
  }

  get currentContentItem(): any {
    if (this.contentList.length > 0 && this.currentPageIndex < this.contentList.length) {
      return this.contentList[this.currentPageIndex];
    }
    return null;
  }

  get displayText(): string {
    // If we have content items, show the current one
    if (this.currentContentItem) {
      return this.currentContentItem.content || '';
    }

    return this.defaultDescription;
  }

  get displayTitle(): string {
    if (this.currentContentItem && this.currentContentItem.title) {
      return this.currentContentItem.title;
    }
    return 'Search Kachiing';
  }

  get displayIcon(): string {
    if (this.currentContentItem && this.currentContentItem.icon) {
      return this.currentContentItem.icon;
    }
    return '';
  }

  get displayColor(): string | null {
    if (this.currentContentItem && this.currentContentItem.color) {
      return this.currentContentItem.color;
    }
    return null;
  }

  getColorHex(colorValue: string): string {
    // Color mapping to actual hex values
    const colorMap: { [key: string]: string } = {
      'primary': '#592880',
      'secondary': '#b32596',
      'emerald': '#10b981',
      'indigo': '#4f46e5',
      'violet': '#7c3aed',
      'teal': '#14b8a6',
      'blue': '#3b82f6',
      'purple': '#9333ea',
      'pink': '#ec4899',
      'cyan': '#06b6d4',
    };
    return colorMap[colorValue] || '#4EC67F'; // Default to emerald green if not found
  }

  getColorStyle(colorValue: string | null): any {
    if (!colorValue) {
      return null;
    }

    // For primary and secondary, use CSS variables
    if (colorValue === 'primary') {
      return { color: 'var(--primary)' };
    }
    if (colorValue === 'secondary') {
      return { color: 'var(--secondary)' };
    }

    // For other colors, use the hex value
    return { color: this.getColorHex(colorValue) };
  }

  getButtonBackgroundColor(colorValue: string | null): string {
    if (!colorValue) {
      return '#4EC67F'; // Default emerald green
    }

    // For primary and secondary, use CSS variables (they work in inline styles too)
    if (colorValue === 'primary') {
      return 'var(--primary)';
    }
    if (colorValue === 'secondary') {
      return 'var(--secondary)';
    }

    // For other colors, use the hex value
    return this.getColorHex(colorValue);
  }

  get isLastPage(): boolean {
    return this.currentPageIndex >= this.contentList.length - 1;
  }

  get currentPageShowTags(): boolean {
    if (this.contentList.length > 0 && this.currentPageIndex < this.contentList.length) {
      const currentItem = this.contentList[this.currentPageIndex];
      return currentItem.showTags !== undefined ? currentItem.showTags : true;
    }
    // If no content items, use the global showTags setting
    return this.showTags;
  }

  get pageIndicator(): string {
    if (this.contentList.length > 0) {
      return `${this.currentPageIndex + 1} / ${this.contentList.length}`;
    }
    return '';
  }

}
